import { Link } from 'react-router';
import { Tag } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { money, qty } from '@/lib/format';
import { Thumb, productCover } from './kit/Thumb';
import { availClass, bundleState } from './status';
import { defaultPackaging, defaultQuote, isGroup, perPackage, packagingLabel, ruleText, type BundleFit } from './rules';
import type { Bundle, ProductListItem } from './types';
import s from './Cards.module.css';

/** 产品卡：名称 + 可用库存（右上，负数红）/ 现有·预占·参考价 / 默认包装（箱）+ 默认供应商报价（高保真 R1） */
export function ProductCard({ p, to, selected }: { p: ProductListItem; to: string; selected?: boolean }) {
  const sum = p.inventorySummary;
  const pk = defaultPackaging(p.packagingOptions);
  const q = defaultQuote(pk);
  const cover = productCover(p);
  return (
    <Link to={to} className={[s.card, selected && s.sel].filter(Boolean).join(' ')}>
      <div className={s.h}>
        <Thumb src={cover} alt={p.name} className={!cover ? s.noCover : undefined} />
        <div className={s.m}>
          <b>{p.name}</b>
          <small>{p.code}{p.category ? ` · ${p.category}` : ' · 未分类'}</small>
        </div>
        <div className={s.q}>
          <small>可用</small>
          <b className={availClass(sum.availableQuantity)}>{qty(sum.availableQuantity)}</b>
        </div>
      </div>
      <div className={s.n}>
        <span><i>现有</i><b>{qty(sum.actualQuantity)}</b></span>
        <span><i>预占</i><b>{qty(sum.reservedQuantity)}</b></span>
        <span className={s.r}><i>参考价</i><b>{money(p.salePrice)}</b></span>
      </div>
      {pk ? (
        <div className={s.f}>
          <Icon name="carton" size={14} />
          <b>{perPackage(pk) || packagingLabel(pk)}</b>
          {q && <span>{q.supplier?.name || '未命名供应商'} {money(q.quotePrice)}</span>}
          {!q && <span className="em">无供应商报价</span>}
        </div>
      ) : (
        <div className={`${s.f} ${s.warn}`}><Icon name="carton" size={14} />未设包装方案</div>
      )}
    </Link>
  );
}

/** 组件构成小块：按组计的蓝底（「每 20 套 1 个」），按套计的灰底 */
export function Uses({ b, max = 4 }: { b: Bundle; max?: number }) {
  const shown = b.items.slice(0, max);
  return (
    <div className={s.uses}>
      {shown.map((i) => (
        <span key={i.id} className={isGroup(i) ? s.grp : undefined}>{i.product.name} · {ruleText(i)}</span>
      ))}
      {b.items.length > max && <span>+{b.items.length - max} 项</span>}
    </div>
  );
}

/** 套装卡：右上可配齐套数（按单仓可用库存算，写明卡在哪个组件）；套装本身不记库存（高保真 R5） */
export function BundleCard({ b, fit, to, selected }: { b: Bundle; fit?: BundleFit; to: string; selected?: boolean }) {
  const st = bundleState(b.isActive);
  return (
    <Link to={to} className={[s.card, selected && s.sel].filter(Boolean).join(' ')}>
      <div className={s.h}>
        <div className={s.m}>
          <b>{b.name}</b>
          <small>{b.code} · {b.items.length} 个组件</small>
        </div>
        {st ? <Tag tone={st.tone} sm>{st.label}</Tag> : fit && (
          <div className={s.q}>
            <small>可配齐</small>
            <b className={fit.sets <= 0 ? 'neg' : ''}>{qty(fit.sets)}<small>套</small></b>
            {fit.limit && <i>卡在{fit.limit.product.name}</i>}
          </div>
        )}
      </div>
      <Uses b={b} />
      <div className={s.d}>
        {b.totalCost != null && <span><i>成本</i><b>{money(b.totalCost)}</b></span>}
        {b.dimensionText && <span className={s.dim}><i>尺寸</i><b>{b.dimensionText}</b></span>}
        {b.totalCost == null && !b.dimensionText && <span className="em">未填成本与尺寸</span>}
      </div>
    </Link>
  );
}
